import { GQL } from '@aline/graphql';
import { entries } from 'remeda';
import { z } from 'zod';

import type { ValidEnv } from './routes';

/**
 * `linkColor` lives in the side-effect-free `util/theme-colors` leaf so the MUI
 * theme can import it without pulling in this module's GraphQL/IIFE fan-out;
 * re-exported here to keep `@aline/common/constants`'s public API unchanged.
 */
export { linkColor } from './util/theme-colors';

export const maxCharacters = 255;
// The name every user's personal org starts with; it says nothing about who they are.
export const PERSONAL_ORG_NAME = 'My Workspace';
export const noWhitespaceRegEx = /^\S+$/g;
export const nonNilGUIDRegEx =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const alineWebsiteURL = 'https://aline.co';
export const defaultCCLicenseUrl = 'http://creativecommons.org/licenses/by/4.0/';
export const MAX_METADATA_IMPORT_UPLOAD_MB = 25;
export const MAX_METADATA_IMPORT_TEMPLATE_DOCUMENTS = 100_000;

/**
 * Hard ceiling for multipart GraphQL `Upload` request bodies, enforced globally
 * by the `graphqlUploadExpress` middleware so resolvers don't each re-check size.
 * Sized to the largest server-streamed upload (the metadata import spreadsheet);
 * individual features may still apply tighter limits client-side.
 */
export const MAX_GRAPHQL_UPLOAD_BYTES = MAX_METADATA_IMPORT_UPLOAD_MB * 1024 * 1024;

/**
 * The type identifier for mention elements in the chat editor.
 * Used by Plate.js to identify mention nodes in the editor.
 */
export const mentionType = 'mention';

export const loginPort = 3031;

export enum MSOfficeMimeType {
  COMPOUND_FILE = 'application/x-cfb',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  PPT = 'application/vnd.ms-powerpoint',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export enum OtherMimeType {
  APNG = 'image/apng',
  BMP = 'image/bmp',
  CSV = 'text/csv',
  GIF = 'image/gif',
  JPEG = 'image/jpeg',
  PDF = 'application/pdf',
  PNG = 'image/png',
  SVG = 'image/svg+xml',
  TEXT_PLAIN = 'text/plain',
  TIFF = 'image/tiff',
  WEBP = 'image/webp',
}

export type MimeType = MSOfficeMimeType | OtherMimeType;
export const MimeType = { ...MSOfficeMimeType, ...OtherMimeType };

/**
 * We only OCR/extract PDF files for now...
 */
export function getIsFileExtractable(fileMimeType: string): boolean {
  return fileMimeType === MimeType.PDF;
}

export const ALINE_DOC_LABEL = 'Aline Doc';

/**
 * `as const satisfies …` keeps each value as its literal type (so we can
 * derive {@link MimeTypeLabel} below) while still requiring exhaustive
 * coverage of every {@link MimeType} member at compile time. If you add a
 * mimetype to the enums above, this object stops compiling until you add
 * the matching label.
 */
const MIME_TO_LABEL = {
  [MSOfficeMimeType.COMPOUND_FILE]: 'DOC',
  [MSOfficeMimeType.DOC]: 'DOC',
  [MSOfficeMimeType.DOCX]: 'DOCX',
  [MSOfficeMimeType.PPT]: 'PPT',
  [MSOfficeMimeType.PPTX]: 'PPTX',
  [MSOfficeMimeType.XLS]: 'XLS',
  [MSOfficeMimeType.XLSX]: 'XLSX',
  [OtherMimeType.APNG]: 'APNG',
  [OtherMimeType.BMP]: 'BMP',
  [OtherMimeType.CSV]: 'CSV',
  [OtherMimeType.GIF]: 'GIF',
  [OtherMimeType.JPEG]: 'JPEG',
  [OtherMimeType.PDF]: 'PDF',
  [OtherMimeType.PNG]: 'PNG',
  [OtherMimeType.SVG]: 'SVG',
  [OtherMimeType.TEXT_PLAIN]: 'TXT',
  [OtherMimeType.TIFF]: 'TIFF',
  [OtherMimeType.WEBP]: 'WEBP',
} as const satisfies Record<MimeType, string>;

/** The set of literal labels produced by {@link MIME_TO_LABEL}. */
export type MimeTypeLabel = (typeof MIME_TO_LABEL)[MimeType];

/**
 * Every label that can appear in the file-type filter dropdown — the
 * static mimetype labels plus the "Aline Doc" sentinel for the NULL
 * mimetype branch.
 */
export type FileTypeLabel = MimeTypeLabel | typeof ALINE_DOC_LABEL;

export type FileTypeFilterOption = {
  label: FileTypeLabel;
  mimetype: MimeType | null;
};

/**
 * Typed view over `entries(MIME_TO_LABEL)`. The single `unknown` cast
 * works around a TS string-enum quirk: `MIME_TO_LABEL`'s literal keys
 * (e.g. `"application/pdf"`) are *not* assignable to the nominal
 * {@link MimeType} enum type, even though `OtherMimeType.PDF === "application/pdf"`
 * at runtime. The cast is sound because every key was placed via an enum
 * member literal, enforced by the `satisfies Record<MimeType, …>` clause
 * on `MIME_TO_LABEL`.
 */
function mimeToLabelEntries(): Array<[MimeType, MimeTypeLabel]> {
  return entries(MIME_TO_LABEL) as unknown as Array<[MimeType, MimeTypeLabel]>;
}

/**
 * File-type filter dropdown options. Labels match {@link getMimeTypeDisplayLabel} / grid valueGetter.
 */
export const FILE_TYPE_FILTER_OPTIONS: readonly FileTypeFilterOption[] = (() => {
  const representativeByMimetypeLabel = new Map<MimeTypeLabel, MimeType>();
  for (const [mimetype, label] of mimeToLabelEntries()) {
    const existing = representativeByMimetypeLabel.get(label);
    if (!existing) {
      representativeByMimetypeLabel.set(label, mimetype);
    } else if (label === 'DOC' && mimetype === MSOfficeMimeType.DOC) {
      representativeByMimetypeLabel.set(label, mimetype);
    }
  }
  const fromMimeTypes: FileTypeFilterOption[] = [...representativeByMimetypeLabel.entries()].map(
    ([label, mimetype]) => ({ label, mimetype })
  );
  return [{ label: ALINE_DOC_LABEL, mimetype: null } as const, ...fromMimeTypes].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
})();

function getIsMimeType(value: string): value is MimeType {
  return value in MIME_TO_LABEL;
}

/**
 * Returns true iff `value` is one of the literal labels in
 * {@link FILE_TYPE_FILTER_OPTIONS}. Useful for narrowing user/filter
 * input from `string` to {@link FileTypeLabel} before passing it to
 * {@link expandFileTypeLabelsToMimetypes}.
 */
export function isFileTypeLabel(value: string): value is FileTypeLabel {
  if (value === ALINE_DOC_LABEL) return true;
  for (const [, label] of mimeToLabelEntries()) {
    if (value === label) return true;
  }
  return false;
}

export function getMimeTypeDisplayLabel(
  mimetype: string | null | undefined
): FileTypeLabel | string {
  if (!mimetype) return ALINE_DOC_LABEL;

  if (getIsMimeType(mimetype)) return MIME_TO_LABEL[mimetype];

  // Fallback: extract extension from mimetype (e.g., "application/pdf" -> "PDF")
  const parts = mimetype.split('/');
  const subtype = parts[1] ?? mimetype;
  // Handle cases like "vnd.openxmlformats-officedocument.wordprocessingml.document"
  const lastPart = subtype.split('.').pop() ?? subtype;
  return lastPart.toUpperCase();
}

/** Separator used in PDF field names for checkbox option widgets: variableNodeId + SEP + optionId. */
export const CHECKBOX_OPTION_NODEID_SEPARATOR = '__';

export const CHECKBOX_CHECKED = 'Yes' as const;
export const CHECKBOX_UNCHECKED = 'No' as const;
export const CHECKBOX_BORDER_THICKNESS_PX = 1;

export const PDF_CHECKBOX_UNCHECKED = 'Off' as const;

export type CheckboxStateText = typeof CHECKBOX_CHECKED | typeof CHECKBOX_UNCHECKED;

export function isCheckboxCheckedText(text: string | undefined): boolean {
  return text === CHECKBOX_CHECKED;
}

export function checkboxStateText(isChecked: boolean): CheckboxStateText {
  return isChecked ? CHECKBOX_CHECKED : CHECKBOX_UNCHECKED;
}

/**
 * Expand a set of display labels into the canonical mimetype set used by
 * file-type filtering. The label-side of the file-type filter is what the
 * user picked (e.g. "DOC"), but the cache stores raw mimetypes — and a
 * single label can map to multiple mimetypes
 * (e.g. "DOC" → both `application/msword` and `application/x-cfb`).
 *
 * Accepting `readonly FileTypeLabel[]` (not `readonly string[]`) means a
 * typo at the call site fails to compile rather than silently returning
 * an empty mimetype set at runtime.
 *
 * Returns:
 *   - `mimetypes`: every static mimetype whose `getMimeTypeDisplayLabel`
 *     output is in `labels`. Used in SQL as `mimetype = ANY(mimetypes)`.
 *   - `includeNull`: true iff `labels` contains {@link ALINE_DOC_LABEL},
 *     since native Aline Docs are stored with a NULL mimetype.
 */
export function expandFileTypeLabelsToMimetypes(labels: readonly FileTypeLabel[]): {
  includeNull: boolean;
  mimetypes: MimeType[];
} {
  const labelSet = new Set<FileTypeLabel>(labels);
  const includeNull = labelSet.has(ALINE_DOC_LABEL);
  const mimetypes: MimeType[] = [];
  for (const [mimetype, label] of mimeToLabelEntries()) {
    if (labelSet.has(label)) mimetypes.push(mimetype);
  }
  return { includeNull, mimetypes };
}

export enum PDFCustomDataKey {
  cannedFieldId = 'cannedFieldId',
  checkboxOptionLabel = 'checkboxOptionLabel',
  nodeId = 'nodeId',
  searchHighlight = 'searchHighlight',
  signatureImgId = 'signatureImgId',
  userSetAnnotHeight = 'userSetAnnotHeight',
  userSetAnnotWidth = 'userSetAnnotWidth',
}

/**
 * The mime types that are used in AI Chat and Markdown code blocks.
 */
export const aiMimeTypes = {
  csvSuggestionList: 'csv-suggestion-list',
  htmlDraftDocument: 'html-draft-document',
  jsonAlert: 'json-alert',
  jsonError: 'json-error',
  jsonPlaybookGeneration: 'json-playbook-generation',
  jsonPlaybookRun: 'json-playbook-run',
  jsonReportGeneration: 'json-report-generation',
  jsonToolCall: 'json-tool-call',
  jsonWorkflowResult: 'json-workflow-result',
  jsonlRedlineEdit: 'jsonl-redline-edit',
  jsonlSuggestionList: 'jsonl-suggestion-list',
  markdownDraftDocument: 'markdown-draft-document',
  plainText: 'plain-text',
  textThinking: 'text-thinking',
} as const;

/**
 * The mime types that are available in the client. Some are server-side only.
 */
export const clientSideMimeTypes = [
  aiMimeTypes.jsonAlert,
  aiMimeTypes.jsonError,
  aiMimeTypes.jsonPlaybookGeneration,
  aiMimeTypes.jsonPlaybookRun,
  aiMimeTypes.jsonlRedlineEdit,
  aiMimeTypes.htmlDraftDocument,
  aiMimeTypes.jsonReportGeneration,
  aiMimeTypes.jsonlSuggestionList,
  aiMimeTypes.markdownDraftDocument,
  aiMimeTypes.jsonToolCall,
  aiMimeTypes.jsonWorkflowResult,
  aiMimeTypes.textThinking,
] as const;

/**
 * Text used for citation links in document analysis.
 * This constant is used in both client and server packages.
 */
export const citationLinkText = 'Citation';

/**
 * The mime types that are available on the server.
 *
 * These include everything in `clientSideMimeTypes` plus certain server-side
 * only mime types like `csvSuggestionListMimeType` that are converted during
 * streaming, like with `createStreamingCsvReplacer`.
 */
export const serverSideMimeTypes = [...clientSideMimeTypes, aiMimeTypes.csvSuggestionList] as const;

/**
 * The schema for a code block suggestion. Used both in client and server.
 */
export const codeBlockSuggestionSchema = z.object({
  category: z.string(),
  documentId: z.string(),
  groupId: z.string(),
  locationId: z.string(),
  original: z.string(),
  reason: z.string(),
  schemaVersion: z.enum(['2025-04-01']).default('2025-04-01'),
  suggested: z.string(),
  textHash: z.string(),
  title: z.string(),
  /** NOTE: Add 'insert' and 'remove' later. */
  type: z.enum(['edit']),
  versionId: z.string(),
});
export type CodeBlockSuggestion = z.infer<typeof codeBlockSuggestionSchema>;

export const codeBlockSuggestionErrorSchema = z.object({
  documentId: z.string(),
  id: z.string(),
  locationId: z.string(),
  message: z.string(),
  schemaVersion: z.enum(['2025-04-01']).default('2025-04-01'),
  title: z.string(),
  type: z.literal('suggested-edit-error'),
});
export type CodeBlockSuggestionError = z.infer<typeof codeBlockSuggestionErrorSchema>;

/**
 * The schema for AI-generated errors shown in chat.
 */
export const aiErrorSchema = z.object({
  message: z.string(),
  stack: z.string(),
  title: z.string(),
});
export type AIError = z.infer<typeof aiErrorSchema>;

export const temporalConstants = {
  backlogCountMetricName: 'ActivityTaskQueueBacklogCount',
  backlogIncreaseRateMetricName: 'ActivityTaskQueueBacklogIncreaseRate',
  defaultActivityHeartbeatIntervalMs: 30_000,
  maxAiActivitySlots: 500,
} as const;

export function isUnreviewedRelationship(r: {
  review: { outcome: GQL.ReviewOutcome } | null;
}): boolean {
  return r.review === null;
}

export function isConfirmedRelationship(r: {
  review: { outcome: GQL.ReviewOutcome } | null;
}): boolean {
  return r.review?.outcome === GQL.ReviewOutcome.Confirmed;
}

export function isNotRejectedRelationship(r: {
  review: { outcome: GQL.ReviewOutcome } | null;
}): boolean {
  return r.review?.outcome !== GQL.ReviewOutcome.Rejected;
}

export function createCloudwatchNamespace(nodeEnv: ValidEnv): string {
  return `aline-temporal-metrics-${nodeEnv.toLowerCase()}`;
}
